<template>
  <div>
    <div class="property-compare q-mx-auto">
      <organization-paused v-if="displayOrganizationPaused" />
      <template v-else-if="!displayNotSubscribed">
        <q-card flat bordered>
          <q-card-section class="flex items-center justify-between">
            <div class="text-h6">Property Reports</div>
            <q-space />
            <div id="intercom-dashboard-filters" class="row items-start">
              <div class="row items-center q-my-auto">
                <q-btn-dropdown
                  v-if="bulkEditMode"
                  :label="`Bulk Actions (${selected.length})`"
                  no-caps
                  unelevated
                  class="q-mr-sm"
                  :class="$q.dark.isActive ? 'bg-grey-9 text-grey-4' : 'bg-grey-3 text-grey-8'"
                  style="font-weight: 400"
                >
                  <q-list>
                    <q-item-label header>
                      Run Actions (on {{ editableRowsCount }} editable reports)
                      <q-icon name="info" size="12px" class="q-ml-xs" />
                      <q-tooltip>
                        Reports that you are not allowed to edit will be left unchanged. For
                        example, you may have 'Viewer' access.
                      </q-tooltip>
                    </q-item-label>
                    <q-separator />
                    <q-item v-if="hasPropertyReports" v-close-popup clickable @click="subscribeAll">
                      <q-item-section avatar>
                        <q-icon name="schedule_send" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Subscribe</q-item-label>
                        <q-item-label caption>
                          Add yourself to the email list of selected reports
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item v-close-popup clickable @click="unsubscribeAll">
                      <q-item-section avatar>
                        <q-icon name="cancel_schedule_send" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Unsubscribe</q-item-label>
                        <q-item-label caption>
                          Remove yourself from the email list of selected reports
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="hasPropertyReports"
                      v-close-popup
                      clickable
                      @click="changeScheduleDialog = true"
                    >
                      <q-item-section avatar>
                        <q-icon name="schedule" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Change Email Schedule</q-item-label>
                        <q-item-label caption>
                          Change the email schedule for selected reports
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="hasPropertyReports"
                      v-close-popup
                      clickable
                      @click="setLabelDialog = true"
                    >
                      <q-item-section avatar>
                        <q-icon name="label" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Add Tag</q-item-label>
                        <q-item-label caption> Add a tag to selected reports </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="hasPropertyReports"
                      v-close-popup
                      clickable
                      @click="removeLabelDialog = true"
                    >
                      <q-item-section avatar>
                        <q-icon name="label_off" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Remove Tag</q-item-label>
                        <q-item-label caption> Remove a tag from selected reports </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item v-if="billedPerUnit" v-close-popup clickable @click="addAllToPortfolio">
                      <q-item-section avatar>
                        <q-icon name="folder_special" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Add to Portfolio</q-item-label>
                        <q-item-label caption> Add reports to your portfolio </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="billedPerUnit"
                      v-close-popup
                      clickable
                      @click="removeAllFromPortfolio"
                    >
                      <q-item-section avatar>
                        <q-icon name="folder_off" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Remove from Portfolio</q-item-label>
                        <q-item-label caption> Remove report from your portfolio </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="hasPropertyReports"
                      v-close-popup
                      clickable
                      @click="deleteSelectedDialog = true"
                    >
                      <q-item-section avatar class="text-red">
                        <q-icon name="delete" />
                      </q-item-section>
                      <q-item-section>
                        <q-item-label>Permanently Delete Report</q-item-label>
                        <q-item-label caption> Delete selected reports </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
                <q-btn
                  v-if="!isAdmin"
                  no-caps
                  dense
                  unelevated
                  class="q-mr-sm"
                  icon="refresh"
                  size="sm"
                  @click="refreshSubscriptions()"
                />
                <q-btn-dropdown
                  v-else
                  split
                  dense
                  unelevated
                  icon="refresh"
                  size="sm"
                  @click="refreshSubscriptions()"
                >
                  <q-list>
                    <q-item v-close-popup clickable @click="refreshSubscriptions()">
                      <q-item-section>
                        <q-item-label>Default Refresh</q-item-label>
                        <q-item-label caption>
                          Refresh to see your default view, including your reports and those shared
                          with you.
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                    <q-item v-close-popup clickable @click="refreshSubscriptions(true)">
                      <q-item-section>
                        <q-item-label>Admin Refresh</q-item-label>
                        <q-item-label caption>
                          Refresh to access the admin view, showing all reports across your
                          organization.
                        </q-item-label>
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-btn-dropdown>
                <q-btn
                  v-if="$q.screen.gt.sm"
                  dense
                  unelevated
                  icon="download"
                  size="sm"
                  class="q-mr-sm"
                  @click="downloadCSV"
                  :disable="!filteredRows.length"
                >
                  <q-tooltip>Download CSV</q-tooltip>
                </q-btn>
                <q-btn dense unelevated icon="filter_list" class="q-mr-sm">
                  <q-menu transition-duration="100" style="min-width: 200px">
                    <q-list>
                      <q-item-label header> Report Filters </q-item-label>
                      <q-select
                        v-model="filteredStates"
                        transition-duration="100"
                        dense
                        filled
                        multiple
                        clearable
                        label="Filter by State"
                        :options="filteredStateOptions"
                        :option-label="(_) => `${_.state} (${_.count})`"
                        style="width: 200px"
                      />
                      <q-select
                        v-model="filteredCities"
                        transition-duration="100"
                        dense
                        filled
                        clearable
                        multiple
                        :option-label="(_) => `${_.city}, ${_.state} (${_.count})`"
                        label="Filter by City"
                        :options="allCitiesInUse"
                        style="width: 200px"
                      />
                      <q-select
                        v-model="filteredZipCodes"
                        transition-duration="100"
                        dense
                        filled
                        clearable
                        multiple
                        :option-label="(_) => `${_.zipCode} (${_.count})`"
                        label="Filter by Zip Code"
                        :options="allZipCodesInUse"
                        style="width: 200px"
                      />
                      <q-select
                        v-model="filteredEmailActive"
                        transition-duration="100"
                        dense
                        filled
                        clearable
                        label="Filter by Email Active"
                        :options="[
                          { label: 'Active', value: true },
                          { label: 'Disabled', value: false }
                        ]"
                        style="width: 200px"
                      />
                      <q-select
                        v-model="filteredIsInPortfolio"
                        transition-duration="100"
                        dense
                        filled
                        clearable
                        label="Filter by in Portfolio"
                        :options="[
                          { label: 'In Portfolio', value: true },
                          { label: 'Not in Portfolio', value: false },
                          { label: 'Unmarked', value: null }
                        ]"
                        style="width: 200px"
                      />
                      <q-item
                        v-if="showMetadataFiltering"
                        clickable
                        dense
                        class="q-px-sm default-bg"
                        :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-8'"
                        @click="addMetadataFilter"
                      >
                        <span class="row items-center">
                          <q-icon name="add" class="q-mr-xs" />
                          Add Metadata Filter
                        </span>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
              <div v-if="$q.screen.gt.sm" class="col q-gutter-x-sm">
                <div class="row q-gutter-sm">
                  <select-user
                    v-model="filteredOwner"
                    :user-list="allOwners"
                    label="Filter by Report Owner"
                  />
                  <filter-by-tags
                    v-model="filteredTags"
                    :all-current-tags="reportTagsWithCount.map((t) => t.tag)"
                  />
                  <q-input
                    v-model="filter"
                    style="width: 200px"
                    dense
                    filled
                    label="Search Reports..."
                    clearable
                  >
                    <template #prepend>
                      <q-icon name="search" />
                    </template>
                  </q-input>
                </div>
                <metadata-filters
                  v-if="showMetadataFiltering"
                  v-model="activeMetadataFilters"
                  :metadata-keys="metadataKeys"
                />
              </div>
            </div>
          </q-card-section>
          <q-markup-table v-if="loading" bordered dense>
            <thead>
              <tr :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
                <th scope="col" />
                <th v-for="n in 6" :key="n" scope="col">
                  <q-skeleton animation="blink" type="text" />
                </th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in 10" :key="n">
                <td style="width: 33%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td style="width: 10%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td style="width: 9%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td style="width: 5%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td style="width: 12%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td style="width: 8%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td style="width: 10%">
                  <q-skeleton animation="blink" type="text" />
                </td>
                <td class="row justify-end" style="height: 45px">
                  <q-skeleton animation="blink" type="text" style="width: 125px" />
                </td>
              </tr>
            </tbody>
          </q-markup-table>
          <template v-else>
            <q-btn
              dense
              unelevated
              icon="chevron_left"
              size="sm"
              class="absolute no-padding"
              style="margin-top: 5px; margin-left: 4px"
              @click="scrollTags(-1)"
            />
            <q-btn
              dense
              unelevated
              icon="chevron_right"
              size="sm"
              class="absolute no-padding"
              style="margin-top: 5px; margin-right: 4px; right: 0px"
              @click="scrollTags(1)"
            />
            <q-scroll-area ref="tagScrollArea" style="height: 36px" class="q-mb-sm q-mx-lg">
              <div id="intercom-dashboard-reports-by-tag" class="row no-wrap">
                <div v-for="t in reportTagsWithCount" :key="t.tag">
                  <q-chip
                    :key="t.tag"
                    clickable
                    @click="filterByTag(t.tag)"
                    :ripple="false"
                    :class="{ 'active-chip': filteredTags === t.tag }"
                  >
                    {{ t.tag }} ({{ t.count }})
                    <q-btn
                      v-if="hasPropertyReports"
                      dense
                      flat
                      round
                      padding="xs"
                      class="q-ml-xs"
                      :to="{ name: 'Tag Report', query: { tag: t.tag } }"
                    >
                      <q-icon name="description" size="xs" />
                      <q-tooltip> New Report from reports with {{ t.tag }} tag </q-tooltip>
                    </q-btn>
                    <q-btn
                      v-if="canCreateQueryBuilderDatasets && hasPropertyReports"
                      dense
                      flat
                      round
                      padding="xs"
                      :class="{
                        'active-tag-dataset-icon': t.tag === 'Active'
                      }"
                      :to="{
                        name: 'Query Builder',
                        query: {
                          tag: t.tag,
                          admin: isAdminView
                            ? true
                            : undefined /** `undefined` because we want to omit the parameter */
                        }
                      }"
                    >
                      <q-icon name="data_usage" size="xs" />
                      <q-tooltip>
                        New Query Builder dataset from reports with {{ t.tag }} tag
                      </q-tooltip>
                    </q-btn>
                  </q-chip>
                </div>
              </div>
            </q-scroll-area>
            <q-table
              v-model:selected="selected"
              dense
              flat
              bordered
              row-key="id"
              virtual-scroll
              selection="multiple"
              class="dashboard-table"
              :rows="filteredRows"
              :columns="columns"
              :rows-per-page-options="[0]"
              :virtual-scroll-item-size="68"
              :virtual-scroll-sticky-size-start="30"
              :loading="batchUpdateProgress"
              :filter="filter"
              :table-header-class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
              @selection="handleSelection"
            >
              <template #no-data>
                <div v-if="setFilters.length === 0" class="q-mx-auto q-my-md text-center">
                  <q-img src="/empty-state-2.svg" style="width: 100px" class="q-my-lg" />
                  <div class="text-bold" style="font-size: 17px">No Property Reports yet?</div>
                  <div class="text-grey-8" style="font-size: 15px">
                    <span v-if="hasPropertyReports && canCreatePropertyReports">
                      Create a report for any address, it only takes a minute. Or make sure you have
                      been invited to an existing Market Report by your organization.
                    </span>
                    <span v-else-if="!hasPropertyReports">
                      Make sure you have been invited to a Market Report by your organization
                    </span>
                    <span v-else>
                      Reports created by other members in your organization will appear here. Your
                      group does does not have permission to create property reports. Reach out to
                      an organization admin to get access.
                    </span>
                  </div>
                  <div class="q-mx-auto q-my-lg" v-if="canCreatePropertyReports">
                    <q-btn
                      color="primary"
                      no-caps
                      unelevated
                      :to="hasPropertyReports ? { name: 'New Property Report' } : undefined"
                      @click="clickedNewReport('in_table_empty_state')"
                    >
                      <q-icon name="add" class="q-mr-sm" />New Report
                    </q-btn>
                  </div>
                </div>
                <div v-else class="q-mx-auto q-my-md text-center">
                  <div class="text-bold q-mt-md" style="font-size: 17px">
                    No Property Reports match your filters:
                  </div>
                  <div class="text-grey-8 q-mt-sm" style="font-size: 15px">
                    <div v-for="f in setFilters" :key="f">
                      <q-chip color="grey-5" text-color="black">
                        {{ f }}
                      </q-chip>
                    </div>
                  </div>
                  <div class="q-mx-auto q-my-lg"></div>
                </div>
              </template>

              <template #body-cell-image="props">
                <q-td :props="props" class="relative-position">
                  <q-img
                    v-if="props.value && !streetviewErrors[props.row.id]"
                    data-private
                    no-spinner
                    referrerpolicy="no-referrer"
                    :src="props.value"
                    style="width: 100%; height: 66px; object-fit: cover"
                    @error="streetviewErrors[props.row.id] = true"
                  />
                  <div
                    v-if="streetviewErrors[props.row.id] || !props.value"
                    class="bg-grey-3 column justify-center"
                    :class="{ 'bg-grey-9': $q.dark.isActive }"
                    style="width: 100%; height: 66px"
                  >
                    <div class="row justify-center">
                      <q-icon name="apartment" class="text-grey-5" size="lg" />
                    </div>
                  </div>
                  <div
                    v-if="billedPerUnit && props.row.is_in_portfolio === true"
                    class="absolute-bottom-right"
                  >
                    <q-chip dense color="primary" class="text-white">
                      <q-icon name="folder_special" color="white" />
                      <q-tooltip> In Portfolio </q-tooltip>
                    </q-chip>
                  </div>
                </q-td>
              </template>

              <template #body-cell-name="props">
                <q-td :props="props">
                  <div class="row items-center">
                    <router-link
                      :to="{ name: 'Property Report', params: { reportId: props.row.id } }"
                      class="property-link"
                      style="max-width: 200px"
                    >
                      <div class="text-bold text-elipsis">
                        {{ props.value }}
                      </div>
                      <div v-if="props.row.subject" class="text-weight-regular text-elipsis">
                        {{
                          props.row.property_edits?.find(
                            (_) => _.property_id === props.row.subject.id
                          )?.street_address || props.row.subject.street_address
                        }}
                      </div>
                      <div v-if="props.row.subject" class="text-weight-regular">
                        {{ props.row.subject.city }}, {{ props.row.subject.state }}
                        {{ props.row.subject.zip_code }}
                      </div>
                    </router-link>
                    <div class="q-ml-sm">
                      <q-icon v-if="props.row.public_id" name="public">
                        <q-tooltip>Public</q-tooltip>
                      </q-icon>
                      <q-icon
                        v-else-if="props.row.organization_visibility !== null"
                        name="business"
                      >
                        <q-tooltip v-if="organizationName !== undefined">
                          {{ organizationName }}
                        </q-tooltip>
                        <q-tooltip v-else>No Organization</q-tooltip>
                      </q-icon>
                    </div>
                  </div>
                </q-td>
              </template>

              <template #body-cell-user="props">
                <q-td :props="props">
                  <div
                    v-if="props.value"
                    :id="`intercom-${props.rowIndex}-report-owner`"
                    class="flex cursor-pointer"
                    @click="copyEmailAddress(props.value.email)"
                  >
                    <q-tooltip> Click to copy email address {{ props.value.email }} </q-tooltip>
                    <q-avatar size="28px" round class="bg-grey-4">
                      <q-img
                        v-if="props.value?.picture"
                        :src="props.value.picture"
                        referrerpolicy="no-referrer"
                        no-spinner
                      />
                      <q-icon v-else name="person" />
                    </q-avatar>
                    <div v-if="$q.screen.gt.md" class="q-ml-sm q-my-auto">
                      {{ userToName(props.value) }}
                    </div>
                  </div>
                </q-td>
              </template>

              <template #body-cell-tags="props">
                <q-td :props="props">
                  <div :id="`intercom-${props.rowIndex}-report-tags`">
                    <q-select
                      transition-duration="100"
                      :model-value="props.row.tags ? props.row.tags : []"
                      :readonly="
                        !editableRows[props.row.id] ||
                        !hasPropertyReports ||
                        userHasReadOnlyAccess(props.row)
                      "
                      :options="availableReportTags"
                      :color="props.row.tags.length > 1 ? 'primary' : 'accent'"
                      :loading="loadingTagChanges[props.row.id]"
                      dense
                      options-dense
                      outlined
                      multiple
                      style="width: 160px; font-size: 12px"
                      @update:model-value="(x) => updateReportTags(props.row, x)"
                    >
                      <template #prepend>
                        <q-icon name="label" size="14px" />
                      </template>
                      <template #selected>
                        <div style="font-size: 11px" class="text-weight-regular text-elipsis">
                          {{ props.row.tags.join(', ') }}
                        </div>
                      </template>
                    </q-select>
                  </div>
                </q-td>
              </template>

              <template #body-cell-email_schedule="props">
                <q-td :props="props">
                  <template v-if="props.value && props.value.cron_expression !== ''">
                    <q-chip
                      v-for="schedule in humanizeDaysOfWeek(props.value.cron_expression)"
                      :key="schedule"
                      dense
                      color="primary"
                      :ripple="false"
                      text-color="white"
                      class="small-chip"
                    >
                      {{ schedule }}
                    </q-chip>
                  </template>
                  <q-chip v-else class="small-chip" color="grey-7" outline dense :ripple="false">
                    Disabled
                  </q-chip>
                </q-td>
              </template>

              <template #body-cell-shares="props">
                <q-td v-if="$q.screen.gt.lg" :props="props">
                  <div class="flex items-center q-gutter-xs">
                    <q-icon
                      v-if="props.row.organization_visibility !== null"
                      name="corporate_fare"
                      size="20px"
                      class="shares-org-icon"
                    >
                      <q-tooltip>
                        {{
                          organizationName
                            ? `Shared with ${organizationName}`
                            : 'Shared company-wide'
                        }}
                      </q-tooltip>
                    </q-icon>
                    <div v-if="props.value.length > 0 && editableRows[props.row.id]">
                      <!-- Show all avatars if there are 5 or less -->
                      <div
                        v-if="props.value.length <= 5"
                        :id="`intercom-${props.rowIndex}-report-shares`"
                        class="cursor-pointer q-gutter-sm stacked-avatars"
                      >
                        <q-avatar
                          v-for="(accessUser, i) in props.value"
                          :key="i"
                          size="sm"
                          round
                          class="bg-grey-4 shares-avatar"
                          :style="`right: ${i * 20}px`"
                          @click="copyEmailAddress(accessUser.email)"
                        >
                          <q-img
                            v-if="accessUser?.picture"
                            :src="accessUser.picture"
                            referrerpolicy="no-referrer"
                            no-spinner
                          />
                          <q-icon v-else name="person" />
                          <q-tooltip>{{ userToName(accessUser) }}</q-tooltip>
                        </q-avatar>
                      </div>

                      <div
                        v-else
                        :id="`intercom-${props.rowIndex}-report-shares`"
                        class="cursor-pointer q-gutter-sm stacked-avatars"
                      >
                        <q-avatar
                          v-for="(accessUser, i) in props.value.slice(0, 4)"
                          :key="i"
                          size="sm"
                          round
                          class="bg-grey-4 shares-avatar"
                          :style="`right: ${i * 20}px`"
                          @click="copyEmailAddress(accessUser.email)"
                        >
                          <q-img
                            v-if="accessUser?.picture"
                            :src="accessUser.picture"
                            referrerpolicy="no-referrer"
                            no-spinner
                          />
                          <q-icon v-else name="person" />
                          <q-tooltip>{{ userToName(accessUser) }}</q-tooltip>
                        </q-avatar>

                        <!-- Show Remaining Shares Count if applicable -->
                        <q-avatar
                          v-if="props.value.length > 4"
                          size="sm"
                          round
                          class="overlapping bg-grey-4 shares-avatar"
                          :style="`right: ${4 * 20}px`"
                        >
                          <div>+{{ props.value.slice(4).length }}</div>
                          <q-tooltip>
                            <template v-for="(accessUser, i) in props.value.slice(4)" :key="i">
                              {{ userToName(accessUser) }}
                              <br v-if="i < props.value.slice(4).length - 1" />
                            </template>
                          </q-tooltip>
                        </q-avatar>
                      </div>
                    </div>
                  </div>
                </q-td>

                <!-- Smaller view port; Only show if the user can do something with it -->
                <q-td
                  v-else-if="
                    (props.value.length > 0 || props.row.organization_visibility !== null) &&
                    editableRows[props.row.id]
                  "
                  :props="props"
                >
                  <div class="flex items-center q-gutter-sm">
                    <q-icon
                      v-if="props.row.organization_visibility !== null"
                      name="corporate_fare"
                      size="20px"
                    >
                      <q-tooltip>
                        {{
                          organizationName
                            ? `Shared with ${organizationName}`
                            : 'Shared company-wide'
                        }}
                      </q-tooltip>
                    </q-icon>
                    <q-btn-dropdown
                      v-if="props.row.access_users.length > 0"
                      :label="`${props.row.access_users.length}`"
                      no-caps
                      unelevated
                      dense
                      padding="0px"
                      style="font-weight: 400"
                    >
                      <q-item
                        v-for="accessUser in props.row.access_users"
                        :key="accessUser.id"
                        clickable
                        v-close-popup
                        @click="copyEmailAddress(accessUser.email)"
                      >
                        <q-tooltip> Click to copy email address {{ accessUser.email }} </q-tooltip>
                        <q-avatar size="28px" round class="bg-grey-4 shares-avatar">
                          <q-img
                            v-if="accessUser?.picture"
                            :src="accessUser.picture"
                            referrerpolicy="no-referrer"
                            no-spinner
                          />
                          <q-icon v-else name="person" />
                        </q-avatar>
                        <div class="q-ml-sm q-my-auto">
                          {{ userToName(accessUser) }}
                        </div>
                      </q-item>
                    </q-btn-dropdown>
                  </div>
                </q-td>
                <q-td v-else></q-td>
              </template>

              <template #body-cell-actions="props">
                <q-td :props="props">
                  <q-btn
                    :id="`intercom-${props.rowIndex}-report-actions`"
                    icon="more_vert"
                    dense
                    flat
                  >
                    <q-menu transition-duration="100" max-width="170px" auto-close>
                      <div>
                        <template
                          v-if="
                            hasPropertyReports &&
                            editableRows[props.row.id] &&
                            canEditPropertyReports
                          "
                        >
                          <q-btn
                            :disable="userHasReadOnlyAccess(props.row)"
                            flat
                            no-caps
                            square
                            align="left"
                            class="full-width"
                            :to="{
                              name: 'Edit Property Report',
                              params: { reportId: props.row.id }
                            }"
                          >
                            <q-tooltip v-if="userHasReadOnlyAccess(props.row)">
                              You have read only access and cannot edit this report
                            </q-tooltip>
                            <q-icon name="edit" size="15px" class="q-mr-md" />
                            <div>Edit</div>
                          </q-btn>
                          <q-separator />
                        </template>
                        <template v-if="hasPropertyReports && canCreatePropertyReports">
                          <q-btn
                            flat
                            no-caps
                            square
                            align="left"
                            class="full-width"
                            @click="copyPropertyReport(props.row.id)"
                          >
                            <q-icon name="content_copy" size="15px" class="q-mr-md" />
                            <div>Copy</div>
                          </q-btn>
                          <q-separator />
                        </template>
                        <template v-if="hasPropertyReports && editableRows[props.row.id]">
                          <q-btn
                            flat
                            no-caps
                            square
                            align="left"
                            :disable="
                              !props.row.email_schedule ||
                              props.row.email_schedule.emails.length === 0
                            "
                            :loading="getForceRunLoading(props.row.id)"
                            class="full-width"
                            @click="forceRun(props.row.id)"
                          >
                            <q-icon name="mail_outline" size="15px" class="q-mr-md" />
                            <div>Send Email</div>
                            <q-tooltip
                              v-if="
                                !props.row.email_schedule ||
                                props.row.email_schedule.emails.length === 0
                              "
                            >
                              Set an email schedule and add at least one email address for this
                              report
                            </q-tooltip>
                          </q-btn>
                          <q-separator />
                        </template>
                        <template
                          v-if="
                            hasPropertyReports &&
                            editableRows[props.row.id] &&
                            canEditPropertyReports
                          "
                        >
                          <q-btn
                            :disable="!allowedToAdminOrOwner(props.row.user_id)"
                            flat
                            no-caps
                            square
                            align="left"
                            class="full-width"
                            @click="validateDelete(props.row)"
                          >
                            <q-tooltip v-if="!allowedToAdminOrOwner(props.row.user_id)">
                              Only this report's owner can delete it
                            </q-tooltip>
                            <q-icon name="delete" size="15px" class="q-mr-md" />
                            <div>Delete</div>
                          </q-btn>
                          <q-separator />
                        </template>
                        <q-btn
                          :disable="!allowedToLeaveReport(props.row)"
                          flat
                          no-caps
                          square
                          no-wrap
                          align="left"
                          class="full-width"
                          @click="confirmLeaveReport(props.row)"
                        >
                          <q-tooltip v-if="leaveReportButtonTooltip(props.row)">
                            {{ leaveReportButtonTooltip(props.row) }}
                          </q-tooltip>
                          <q-icon name="person_remove" size="15px" class="q-mr-md" />
                          <div>Leave Report</div>
                        </q-btn>
                      </div>
                    </q-menu>
                  </q-btn>
                </q-td>
              </template>
            </q-table>
          </template>
        </q-card>

        <safe-teleport to="#toolbar-portal">
          <q-toolbar
            class="q-px-sm row justify-center"
            :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
            style="height: 75px"
            :style="`border-bottom: 1px solid ${$q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'}`"
          >
            <div class="property-compare full-width row items-center">
              <div style="font-size: 20px; font-weight: 500" class="flex items-center">
                <q-icon name="space_dashboard" class="q-mr-sm" />
                Dashboard
              </div>
              <q-chip
                v-if="propertyReportsTrialDays > 0 && $q.screen.gt.sm"
                :ripple="false"
                class="text-primary q-mx-md"
                outline
              >
                <div class="text-center full-width">
                  Trial ends in<b class="q-ml-xs"
                    >{{
                      propertyReportsTrialDays < 1 ? '<1' : Math.floor(propertyReportsTrialDays)
                    }}
                    day{{ propertyReportsTrialDays > 2 ? 's' : '' }}
                  </b>
                </div>
                <q-tooltip> Trial ends on {{ formatDate(propertyReportsTrialEnd) }} </q-tooltip>
              </q-chip>
              <q-space />
              <q-btn
                v-if="hasPropertyReports && canCreatePropertyReports"
                no-caps
                unelevated
                class="q-mr-md"
                @click="bulkImportDialog = true"
              >
                Bulk Import
              </q-btn>

              <q-btn-dropdown
                v-if="canCreatePropertyReports"
                id="intercom-new-report"
                color="primary"
                no-caps
                unelevated
                split
                :to="hasPropertyReports ? { name: 'New Property Report' } : undefined"
                @click="clickedNewReport('top_right_corner')"
              >
                <template #label> New Report </template>
                <q-item
                  clickable
                  :to="hasPropertyReports ? { name: 'New Development' } : undefined"
                  @click="!hasPropertyReports ? (displayNotSubscribed = true) : undefined"
                >
                  <q-item-section>New Development</q-item-section>
                </q-item>
              </q-btn-dropdown>
            </div>
          </q-toolbar>
        </safe-teleport>
      </template>
      <div v-else class="q-my-xl q-mx-auto" style="max-width: 1280px">
        <q-btn v-if="hasLimitedAccess" flat no-caps dense @click="displayNotSubscribed = false">
          <q-icon name="arrow_back" class="q-mr-sm" />
          Back to Dashboard
        </q-btn>
        <not-subscribed />
      </div>
    </div>
    <q-dialog v-if="bulkImportDialog" v-model="bulkImportDialog" transition-duration="100">
      <bulk-csv-import @finished="finishedImport" />
    </q-dialog>
    <q-dialog v-if="deleteDialog" v-model="deleteDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to permanently delete <b>{{ toDelete?.name }}</b
            >?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            no-caps
            unelevated
            label="Delete"
            color="red"
            :loading="deleteLoading"
            @click="deleteReport"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-if="setLabelDialog" v-model="setLabelDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <div class="text-h6">Add Report Tag</div>
          <div class="text-grey-7">
            Pick a tag to add to selected reports. Only reports you are allowed to edit will be
            labeled. Reports that already have this tag will be unaffected.
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          Enter the tag you want to add to selected reports
          <q-select
            v-model="label"
            :options="availableReportTags"
            dense
            options-dense
            outlined
            class="q-mt-sm"
          />
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            flat
            label="Add Tag"
            no-caps
            unelevated
            @click="
              () => {
                setTagAll(label)
                setLabelDialog = false
                label = null
              }
            "
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-if="removeLabelDialog" v-model="removeLabelDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <div class="text-h6">Remove Tag</div>
          <div class="text-grey-7">
            Pick a tag to remove from all selected reports. Only reports you are allowed to edit
            will be labeled. Reports that don't have this tag will be unaffected.
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          Enter the tag you want to remove from selected reports
          <q-select
            v-model="label"
            :options="availableReportTags"
            dense
            options-dense
            outlined
            class="q-mt-sm"
          />
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            flat
            label="Remove Tag"
            no-caps
            unelevated
            @click="unsetTagAll(label)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-if="changeScheduleDialog" v-model="changeScheduleDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <div class="text-h6">Change Schedule</div>
          <div class="text-grey-7">
            Pick a schedule to change selected reports to. Only reports you are allowed to edit will
            be scheduled.
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          Enter the days of the week you want to schedule selected reports on:
          <div class="row q-mt-md">
            <q-checkbox v-model="scheduleDays" :label="`Monday`" :val="`Monday`" />
          </div>
          <div>
            <q-checkbox v-model="scheduleDays" :label="`Tuesday`" :val="`Tuesday`" />
          </div>
          <div>
            <q-checkbox v-model="scheduleDays" :label="`Wednesday`" :val="`Wednesday`" />
          </div>
          <div>
            <q-checkbox v-model="scheduleDays" :label="`Thursday`" :val="`Thursday`" />
          </div>
          <div>
            <q-checkbox v-model="scheduleDays" :label="`Friday`" :val="`Friday`" />
          </div>
          <div>
            <q-checkbox v-model="scheduleDays" :label="`Saturday`" :val="`Saturday`" />
          </div>
          <div>
            <q-checkbox v-model="scheduleDays" :label="`Sunday`" :val="`Sunday`" />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            flat
            label="Change Schedule"
            no-caps
            unelevated
            @click="changeScheduleAll(scheduleDays)"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-if="leaveReportDialog" v-model="leaveReportDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section class="row items-center">
          <span class="q-ml-sm">
            Are you sure you want to leave <b>{{ reportToLeave.name }}</b
            >?
          </span>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            no-caps
            unelevated
            label="Leave Report"
            color="red"
            :loading="leaveReportLoading"
            @click="leaveReport"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <q-dialog v-if="deleteSelectedDialog" v-model="deleteSelectedDialog" transition-duration="100">
      <q-card style="width: 400px">
        <q-card-section>
          <div class="text-h6">Delete Selected Reports</div>
          <div class="text-grey-7">
            You are about to delete <b>{{ selected.length }}</b> reports. Are you sure you want to
            delete the selected reports? This action cannot be undone.
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section>
          <div class="text-grey-7 q-mb-sm">
            To confirm, please enter the number of reports to delete: <b>{{ selected.length }}</b>
          </div>
          <q-input
            v-model="deleteConfirmationText"
            dense
            outlined
            type="number"
            class="q-mb-sm"
            style="max-width: 200px"
          />
        </q-card-section>
        <q-separator />
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" no-caps unelevated />
          <q-btn
            v-close-popup
            flat
            label="Delete"
            color="red"
            no-caps
            unelevated
            :disable="deleteConfirmationText !== selected.length.toString()"
            @click="deleteSelectedReports"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="showUserInformationModal" persistent>
      <user-information-modal @close="showUserInformationModal = false" />
    </q-dialog>
  </div>
</template>
<script lang="ts">
import { copyToClipboard, type QScrollArea, type QTableColumn } from 'quasar'
import { formatDistance, parseISO, format } from 'date-fns'

import { usePropertyReportStore } from '@/stores/propertyreport'
import { useStripeStore } from '@/stores/stripe'
import { useUserStore } from '@/stores/user'

import { getStreetViewUrl } from 'common'

import NotSubscribed from '@/components/PropertyReports/NotSubscribed.vue'
import BulkCsvImport from '@/components/BulkCsvImport.vue'
import OrganizationPaused from '@/components/OrganizationPaused.vue'

import type { PropertyReportSubscriptionAttributes, AccessUser, UserAttributes } from 'common'
import {
  userToName,
  formatDate,
  cronExpressionToBasicSchedule,
  type BasicSchedule,
  basicScheduleToCronExpression
} from '@/utils'
import SelectUser from '@/components/SelectUser.vue'
import FilterByTags from '@/components/FilterByTags.vue'
import MetadataFilters from '@/components/MetadataFilters.vue'
import { defineAsyncComponent, nextTick, toRaw } from 'vue'
import cronParser, { type DayOfWeekRange } from 'cron-parser'

const UserInformationModal = defineAsyncComponent(
  () => import('@/components/Billing/UserInformationModal.vue')
)

function areBasicSchedulesEqual(schedule1: BasicSchedule[], schedule2: BasicSchedule[]): boolean {
  if (schedule1.length !== schedule2.length) return false
  return schedule1.every((day) => schedule2.includes(day))
}

type MetadataLogicOptions = 'contains' | '=' | '<' | '>' | '<=' | '>='
interface MetadataFilter {
  key: string | null
  value: string | number | boolean | null
  logic: MetadataLogicOptions
}

export default {
  name: 'DashboardView',
  components: {
    FilterByTags,
    SelectUser,
    MetadataFilters,
    NotSubscribed,
    BulkCsvImport,
    OrganizationPaused,
    UserInformationModal
  },
  data() {
    return {
      formatDate,
      userToName,
      storedSelectedRow: null as PropertyReportSubscriptionAttributes | null,
      forceRunLoading: {} as { [id: number]: boolean },
      deleteDialog: false,
      toDelete: null as PropertyReportSubscriptionAttributes | null,
      streetviewErrors: {} as { [id: string]: boolean },
      scheduleDays: [] as BasicSchedule[],
      label: null as string | null,
      displayNotSubscribed: false,
      selected: [] as PropertyReportSubscriptionAttributes[],
      leaveReportDialog: false as boolean,
      reportToLeave: null as PropertyReportSubscriptionAttributes | null,
      activeMetadataFilters: [] as MetadataFilter[],
      debouncedTrackMetadataFilter: null as ReturnType<typeof setTimeout> | null,
      showUserInformationModal: false,

      // loading states
      loading: false,
      deleteLoading: false,
      leaveReportLoading: false,
      batchUpdateProgress: false,
      loadingTagChanges: {} as { [id: number]: boolean },

      // filters
      filter: '',
      filteredTags: null as string | null,
      filteredOwner: null as AccessUser | null,
      filteredStates: null as { state: string; count: number }[] | null,
      filteredCities: null as { city: string; state: string; count: number }[] | null,
      filteredZipCodes: null as { zipCode: string; count: number }[] | null,
      filteredEmailActive: null as { label: string; value: boolean } | null,
      filteredIsInPortfolio: null as { label: string; value: boolean | null } | null,
      filterZipCodeText: '',
      filterCityText: '',
      filterStateText: '',

      // bulk actions
      bulkImportDialog: false,
      setLabelDialog: false,
      removeLabelDialog: false,
      changeScheduleDialog: false,
      deleteSelectedDialog: false,
      deleteConfirmationText: '',

      // query builder bridge
      isPortfolioIconFlashing: false,
      isAdminView: false
    }
  },
  computed: {
    setFilters(): string[] {
      const filters: string[] = []
      // if the user has some filters set, list them here
      if (this.filteredOwner) {
        filters.push(`Owner: ${this.filteredOwner.email}`)
      }
      if (this.filteredTags) {
        filters.push(`Tags: ${this.filteredTags}`)
      }
      if (this.filteredStates) {
        filters.push(`States: ${this.filteredStates.map((_) => _.state).join(', ')}`)
      }
      if (this.filteredCities) {
        filters.push(`Cities: ${this.filteredCities.map((_) => _.city).join(', ')}`)
      }
      if (this.filteredZipCodes) {
        filters.push(`Zip Codes: ${this.filteredZipCodes.map((_) => _.zipCode).join(', ')}`)
      }
      if (this.filteredEmailActive) {
        filters.push(`Email Active: ${this.filteredEmailActive.value}`)
      }
      if (this.filteredIsInPortfolio) {
        filters.push(`Portfolio: ${this.filteredIsInPortfolio.value}`)
      }
      if (this.activeMetadataFilters.length > 0) {
        filters.push(`Metadata: ${this.activeMetadataFilters.map((_) => _.key).join(', ')}`)
      }
      return filters
    },
    displayOrganizationPaused() {
      if (!this.user) {
        return false
      }
      return this.user.pausedOn !== undefined && this.user.pausedOn !== null
    },
    activeSubscriptions() {
      return usePropertyReportStore().activeSubscriptions
    },
    bulkEditMode(): boolean {
      return this.selected.length > 0
    },
    propertyReportsTrialEnd(): string | null {
      const sub = useStripeStore().subscriptions.find((_) =>
        _.products.includes(import.meta.env.VITE_STRIPE_PROPERTY_REPORTS_PRODUCT)
      )
      return sub?.trial_end || null
    },
    propertyReportsTrialDays(): number {
      return useStripeStore().trialDaysLeft || 0
    },
    allStatesInUse() {
      const states: Record<string, number> = {}
      for (const row of this.filteredRows) {
        if (row.subject) {
          if (!states[row.subject.state]) {
            states[row.subject.state] = 1
          } else {
            states[row.subject.state]++
          }
        }
      }
      const result = Object.keys(states).map((state) => ({ state, count: states[state] }))
      result.sort((a, b) => b.count - a.count)
      return result
    },
    filteredStateOptions() {
      if (this.filterStateText === '') {
        return this.allStatesInUse
      }
      return this.allStatesInUse.filter((state) => {
        return state.state.toLowerCase().includes(this.filterStateText.toLowerCase())
      })
    },
    allCitiesInUse() {
      // make a list of all cities and states
      const cities: Record<string, Record<string, number>> = {}
      for (const row of this.filteredRows) {
        if (row.subject) {
          if (!cities[row.subject.state]) {
            cities[row.subject.state] = {}
          }
          if (!cities[row.subject.state][row.subject.city]) {
            cities[row.subject.state][row.subject.city] = 1
          } else {
            cities[row.subject.state][row.subject.city]++
          }
        }
      }
      // sort by count
      const sortedCities: { city: string; state: string; count: number }[] = []
      for (const state in cities) {
        for (const city in cities[state]) {
          sortedCities.push({ city, state, count: cities[state][city] })
        }
      }
      sortedCities.sort((a, b) => b.count - a.count)
      return sortedCities
    },
    filteredCityOptions() {
      return this.allCitiesInUse.filter((city) => {
        return city.city.toLowerCase().includes(this.filterCityText.toLowerCase())
      })
    },
    allZipCodesInUse() {
      const zipCodes: { [zipCode: string]: number } = {}
      for (const row of this.filteredRows) {
        if (row.subject) {
          const zipCode = row.subject.zip_code
          if (zipCode) {
            if (!zipCodes[zipCode]) {
              zipCodes[zipCode] = 1
            } else {
              zipCodes[zipCode]++
            }
          }
        }
      }
      return Object.keys(zipCodes)
        .map((zipCode) => ({ zipCode, count: zipCodes[zipCode] }))
        .sort((a, b) => b.count - a.count)
    },
    filteredZipCodeOptions() {
      return this.allZipCodesInUse.filter((zipCode) => {
        return zipCode.zipCode.toLowerCase().includes(this.filterZipCodeText.toLowerCase())
      })
    },
    reportTagsWithCount(): { count: number; tag: string }[] {
      const reportTags = useUserStore().availableReportTags
      const tags: { [name: string]: number } = {}
      for (const row of this.activeSubscriptions) {
        for (const tag of row.tags) {
          if (reportTags.includes(tag)) {
            if (!tags[tag]) {
              tags[tag] = 0
            }
            tags[tag]++
          }
        }
      }
      return Object.keys(tags)
        .map((tag) => ({ tag, count: tags[tag] }))
        .sort((a, b) => a.tag.localeCompare(b.tag))
    },
    user() {
      return this.$auth0.user?.value
    },
    allOwners(): AccessUser[] {
      const reportUserIds = new Set(this.activeSubscriptions.map((_) => _.user_id))
      return useUserStore().peopleInOrgOrShared.filter((_) => _.id && reportUserIds.has(_.id))
    },
    editableRows(): Record<number, boolean> {
      if (!this.activeSubscriptions) {
        return {}
      }
      const results = {} as Record<number, boolean>
      for (const row of this.activeSubscriptions) {
        results[row.id] = this.reportInOrg(row)
      }
      return results
    },
    editableRowsCount(): number {
      return this.selected.filter(
        ({ id }) =>
          this.editableRows[id] &&
          !this.userHasReadOnlyAccess(this.selected.find((row) => row.id === id))
      ).length
    },
    filteredRows() {
      let result = this.activeSubscriptions.map((_) => {
        return {
          ..._,
          user: this.getUser(_.user_id)
        }
      })

      // filter by owner
      const filteredOwner = this.filteredOwner
      if (filteredOwner !== null) {
        result = result.filter((report) => report.user_id && filteredOwner.id === report.user_id)
      }

      // filter by tags
      const tags = this.filteredTags
      if (tags !== null && tags !== '') {
        result = result.filter((report) => {
          return report.tags.includes(tags)
        })
      }

      // filter by state
      const states = this.filteredStates
      if (states !== null && states.length > 0) {
        result = result.filter((report) => {
          const subjectState = report.subject?.state
          return report.subject && states.some((state) => state.state === subjectState)
        })
      }

      // filter by city
      const cities = this.filteredCities
      if (cities !== null && cities.length > 0) {
        result = result.filter((report) => {
          const subjectCity = report.subject?.city
          const subjectState = report.subject?.state
          return (
            report.subject &&
            cities.some((city) => city.city === subjectCity && city.state === subjectState)
          )
        })
      }

      // filter by zip code
      const zipCodes = this.filteredZipCodes
      if (zipCodes !== null && zipCodes.length > 0) {
        result = result.filter((report) => {
          const subjectZipCode = report.subject?.zip_code
          return report.subject && zipCodes.some((zipCode) => zipCode.zipCode === subjectZipCode)
        })
      }

      // filter by email active
      const emailActive = this.filteredEmailActive
      if (emailActive !== null) {
        result = result.filter(
          (report) => (report.email_schedule === undefined) === emailActive.value
        )
      }

      // filter by is in portfolio status
      const isInPortfolio = this.filteredIsInPortfolio
      if (isInPortfolio !== null) {
        result = result.filter((report) => report.is_in_portfolio === isInPortfolio.value)
      }

      // filter by active metadata filters
      if (this.activeMetadataFilters.length > 0) {
        result = result.filter((report) => {
          if (!report.metadata) return false

          return this.activeMetadataFilters.every((filter) => {
            if (filter.key === null || filter.value === null) return true
            const value = report.metadata[filter.key]

            // Use a debounced version of tracking to avoid multiple rapid-fire events
            if (this.debouncedTrackMetadataFilter) {
              clearTimeout(this.debouncedTrackMetadataFilter)
            }
            this.debouncedTrackMetadataFilter = setTimeout(() => {
              if (filter.value !== '') {
                this.$tracker.changedFilter(
                  'metadata',
                  'dashboard',
                  `${filter.key}.${filter.logic}.${filter.value}`
                )
              }
            }, 1000) // Wait 1 second after last keystroke before tracking

            switch (filter.logic) {
              case 'contains':
                return String(value).toLowerCase().includes(String(filter.value).toLowerCase())
              case '=':
                if (filter.value === 'true' || filter.value === 'false') {
                  return value === (filter.value === 'true')
                }
                return value === filter.value
              case '<':
                return Number(value) < Number(filter.value)
              case '>':
                return Number(value) > Number(filter.value)
              case '<=':
                return Number(value) <= Number(filter.value)
              case '>=':
                return Number(value) >= Number(filter.value)
              default:
                return true
            }
          })
        })
      }

      return result
    },
    availableReportTags(): string[] {
      return useUserStore().availableReportTags
    },
    usesSSO(): boolean {
      return useUserStore().organization?.connection_id !== null
    },
    organizationName(): string | undefined {
      return useUserStore().organization?.display_name
    },
    hasPropertyReports(): boolean {
      return useStripeStore().hasPropertyReports
    },
    hasLimitedAccess(): boolean {
      return useStripeStore().hasLimitedAccess
    },
    columns() {
      const cols: QTableColumn[] = [
        {
          field: 'name',
          name: 'name',
          label: 'Report Name',
          sortable: true,
          align: 'left'
        },
        {
          label: 'Report Tags',
          field: 'tags',
          name: 'tags',
          sortable: true,
          align: 'left'
        },
        {
          label: 'Year Built',
          field: (row: PropertyReportSubscriptionAttributes) => {
            const userEdits = row.property_edits?.find((_) => _.property_id === row.subject?.id)
            return userEdits?.year_built || row.subject?.year_built
          },
          name: 'year_built',
          sortable: true,
          align: 'right'
        },
        {
          label: '# Units',
          field: (row: PropertyReportSubscriptionAttributes) => {
            const userEdits = row.property_edits?.find((_) => _.property_id === row.subject?.id)
            return userEdits?.number_units || row.subject?.number_units
          },
          name: 'number_units',
          sortable: true,
          align: 'right'
        },
        {
          field: 'user',
          name: 'user',
          label: 'Owner',
          sortable: true,
          align: 'left',
          sort: (a: UserAttributes, b: UserAttributes) => (a?.id || '').localeCompare(b?.id || '')
        }
      ]
      if (this.$q.screen.gt.md) {
        cols.push(
          {
            field: 'modified_on',
            format: (val: string) => {
              return formatDistance(parseISO(val), new Date(), { addSuffix: true })
            },
            name: 'modified_on',
            label: 'Last Modified',
            sortable: true,
            align: 'left'
          },
          {
            label: 'Email Schedule',
            field: (row: PropertyReportSubscriptionAttributes) => {
              return row.email_schedule
            },
            name: 'email_schedule',
            align: 'left'
          }
        )
      }
      if (this.$q.screen.gt.sm) {
        if (this.hasPropertyReports) {
          cols.push({
            field: 'access_users',
            name: 'shares',
            label: 'Shares',
            align: 'left'
          })
        }
        cols.push({
          name: 'actions',
          label: 'Actions',
          field: 'actions'
        })
      }
      if (this.$q.screen.gt.sm) {
        return [
          {
            name: 'image',
            field: (row: PropertyReportSubscriptionAttributes) => {
              if (row.subject === null) {
                return null
              }
              const userEdits = row.property_edits?.find((_) => _.property_id === row.subject?.id)
              if (userEdits?.street_view_url) {
                return userEdits.street_view_url
              }
              const street_address = userEdits?.street_address || row.subject.street_address
              const { city, state, zip_code } = row.subject
              const address = [street_address, city, state, zip_code].join(', ')
              if (this.streetviewErrors[row.id]) {
                return getStreetViewUrl(
                  { ...row.subject, street_address },
                  import.meta.env.VITE_API_URL
                )
              }
              return `https://storage.googleapis.com/hello-data-ai-app-frontend/streetviews/${address}.jpeg`
            },
            label: '',
            align: 'left',
            style: 'width: 109px; margin: 0px; padding: 0px; padding-left: 9px'
          } as QTableColumn
        ].concat(cols)
      }
      return cols
    },
    isAdmin() {
      return useUserStore().isAdmin
    },
    hasAPI(): boolean {
      return useStripeStore().hasAPI
    },
    metadataKeys(): string[] {
      return this.activeSubscriptions.reduce((acc: string[], curr) => {
        if (curr.metadata) {
          const keys = Object.keys(curr.metadata)
          keys.forEach((key) => {
            if (!acc.includes(key)) {
              acc.push(key)
            }
          })
        }
        return acc
      }, [] as string[])
    },
    showMetadataFiltering() {
      return this.hasAPI && this.metadataKeys.length > 0
    },
    canCreatePropertyReports() {
      return useUserStore().canCreatePropertyReports
    },
    canEditPropertyReports() {
      return useUserStore().canEditPropertyReports
    },
    canCreateQueryBuilderDatasets() {
      return useUserStore().canCreateQueryBuilderDatasets
    },
    billedPerUnit() {
      return useStripeStore().billedPerUnit
    }
  },
  watch: {
    filteredStates() {
      this.selected = []
    },
    filteredCities() {
      this.selected = []
    },
    filteredZipCodes() {
      this.selected = []
    },
    filteredEmailActive() {
      this.selected = []
    },
    filteredIsInPortfolio() {
      this.selected = []
    },
    activeMetadataFilters() {
      this.selected = []
    },
    filteredOwner(val: AccessUser) {
      localStorage.setItem('filteredOwner', JSON.stringify(val))
      this.selected = []
    },
    filteredTags(val: string | null) {
      if (val === null || val === '') {
        localStorage.removeItem('filteredTags')
      } else {
        localStorage.setItem('filteredTags', JSON.stringify(val))
      }
      this.selected = []
    },
    hasPropertyReports() {
      this.displayNotSubscribed = !this.hasPropertyReports && !this.hasLimitedAccess
    },
    hasLimitedAccess() {
      this.displayNotSubscribed = !this.hasPropertyReports && !this.hasLimitedAccess
    }
  },
  mounted() {
    const savedOwners = localStorage.getItem('filteredOwner')
    if (savedOwners !== null) {
      this.filteredOwner = JSON.parse(savedOwners) as AccessUser
    }

    const savedTags = localStorage.getItem('filteredTags')
    if (savedTags !== null && savedTags !== '' && savedTags !== 'null') {
      try {
        this.filteredTags = JSON.parse(savedTags) as string
      } catch (e) {
        console.error('Error parsing saved tags from localStorage:', e)
        // Clear invalid data from localStorage
        localStorage.removeItem('filteredTags')
      }
    }
  },
  created() {
    const reportsRetrieved = usePropertyReportStore().retrievedReports
    if (
      (!this.activeSubscriptions.length || !reportsRetrieved) &&
      (this.hasPropertyReports || this.hasLimitedAccess)
    ) {
      this.refreshSubscriptions()
    }
    this.displayNotSubscribed = !this.hasPropertyReports && !this.hasLimitedAccess

    const user = this.user
    if (user) {
      if (!user.jobTitle) {
        this.showUserInformationModal = true
      } else if (!this.usesSSO && (!user.given_name || !user.family_name)) {
        this.showUserInformationModal = true
      }
    }
  },
  methods: {
    handleSelection(details: {
      rows: readonly any[]
      keys: readonly any[]
      added: boolean
      evt: Event
    }) {
      const { rows, added, evt } = details
      // ignore selection change from header of not from a direct click event
      if (rows.length !== 1 || evt === void 0) {
        return
      }

      const oldSelectedRow = this.storedSelectedRow
      const [newSelectedRow] = rows as PropertyReportSubscriptionAttributes[]
      const { ctrlKey, shiftKey, metaKey } = evt as MouseEvent

      if (shiftKey !== true) {
        this.storedSelectedRow = newSelectedRow
        return
      }

      const allRows = this.filteredRows

      // wait for the default selection to be performed
      void nextTick(() => {
        if (shiftKey === true) {
          let firstIndex = allRows.findIndex((row) => row.id === oldSelectedRow?.id)
          let lastIndex = allRows.findIndex((row) => row.id === newSelectedRow.id)

          if (firstIndex < 0) {
            firstIndex = 0
          }

          if (firstIndex > lastIndex) {
            ;[firstIndex, lastIndex] = [lastIndex, firstIndex]
          }

          const rangeRows = allRows.slice(firstIndex, lastIndex + 1)
          // we need the original row object so we can match them against the rows in range
          const selectedRows = this.selected.map(toRaw)

          this.selected =
            added === true
              ? selectedRows.concat(
                  rangeRows.filter((row: any) => selectedRows.includes(row) === false)
                )
              : selectedRows.filter((row: any) => rangeRows.includes(row) === false)
        } else if ((ctrlKey || metaKey) !== true && added === true) {
          this.selected = [newSelectedRow]
        }
      })
    },
    allowedToAdminOrOwner(recordUserId: string | undefined) {
      return useUserStore().allowedToAdmin(recordUserId, true)
    },
    async setTagAll(newLabel: string | null) {
      if (newLabel === null) {
        return
      }
      this.batchUpdateProgress = true
      const updatePromises: Promise<PropertyReportSubscriptionAttributes | null>[] = []

      for (const row of this.selected) {
        if (row && !row.tags.includes(newLabel) && !this.userHasReadOnlyAccess(row)) {
          const updatedTags = [...row.tags, newLabel]
          this.loadingTagChanges[row.id] = true

          const relevantReport = this.activeSubscriptions.find((_) => _.id === row.id)
          if (!relevantReport) {
            throw new Error('Report not found')
          }

          updatePromises.push(
            usePropertyReportStore()
              .update({
                ...relevantReport,
                tags: updatedTags
              })
              .finally(() => {
                this.loadingTagChanges[row.id] = false
              })
          )
        }
      }

      try {
        const updatedRows = await Promise.all(updatePromises)
        this.updateSelectedWithNewData(updatedRows)
      } finally {
        this.batchUpdateProgress = false
      }
      this.label = null
    },
    async unsetTagAll(tag: string | null) {
      if (tag === null) {
        return
      }
      this.batchUpdateProgress = true
      const updatePromises: Promise<PropertyReportSubscriptionAttributes | null>[] = []

      for (const row of this.selected) {
        if (row && row.tags.includes(tag) && !this.userHasReadOnlyAccess(row)) {
          const updatedTags = row.tags.filter((t) => t !== tag)
          this.loadingTagChanges[row.id] = true
          const relevantReport = this.activeSubscriptions.find((_) => _.id === row.id)
          if (!relevantReport) {
            throw new Error('Report not found')
          }
          updatePromises.push(
            usePropertyReportStore()
              .update({
                ...relevantReport,
                tags: updatedTags
              })
              .finally(() => {
                this.loadingTagChanges[row.id] = false
              })
          )
        }
      }

      try {
        const updatedRows = await Promise.all(updatePromises)
        this.updateSelectedWithNewData(updatedRows)
      } finally {
        this.batchUpdateProgress = false
      }
      this.label = null
    },
    subscribeAll() {
      this.$q
        .dialog({
          title: 'Subscribe All',
          message: `Are you sure you want to subscribe to all reports?`,
          cancel: true,
          persistent: true
        })
        .onOk(async () => {
          this.batchUpdateProgress = true
          const userEmail = this.user?.email
          if (!userEmail) {
            this.batchUpdateProgress = false
            return
          }

          const updatePromises: Promise<PropertyReportSubscriptionAttributes | null>[] = []

          for (const row of this.selected) {
            const emailSchedule = row.email_schedule ?? { emails: [], cron_expression: '' }
            if (emailSchedule.emails.includes(userEmail)) {
              // already subscribed and has a schedule, no need to update
              continue
            }

            emailSchedule.emails.push(userEmail)

            if (!emailSchedule.cron_expression) {
              emailSchedule.cron_expression = basicScheduleToCronExpression([
                'Monday'
              ] as BasicSchedule[])!
            }

            // find the report in the active subscriptions to avoid sending extra data
            const relevantReport = this.activeSubscriptions.find((_) => _.id === row.id)
            if (!relevantReport) {
              throw new Error('Report not found')
            }
            updatePromises.push(
              usePropertyReportStore().update({
                ...relevantReport,
                email_schedule: emailSchedule
              })
            )
          }

          try {
            const updatedRows = await Promise.all(updatePromises)
            this.updateSelectedWithNewData(updatedRows)
          } finally {
            this.batchUpdateProgress = false
          }
        })
    },
    unsubscribeAll() {
      this.$q
        .dialog({
          title: 'Unsubscribe All',
          message: `Are you sure you want to unsubscribe from all reports?`,
          cancel: true,
          persistent: true
        })
        .onOk(async () => {
          this.batchUpdateProgress = true
          const userEmail = this.user?.email
          if (!userEmail) {
            throw new Error('User email not found')
          }

          const updatePromises: Promise<PropertyReportSubscriptionAttributes | null>[] = []

          for (const row of this.selected) {
            const emailSchedule = row.email_schedule

            // Skip if there's no email schedule to unsubscribe from.
            if (!emailSchedule) {
              continue
            }

            if (!emailSchedule.emails.includes(userEmail)) {
              // already unsubscribed and has a schedule, no need to update
              continue
            }

            // If this user is on the schedule, then remove them.
            emailSchedule.emails.splice(emailSchedule.emails.indexOf(userEmail), 1)

            // If this was the last person on the list of emails, then disable
            // the email schedule all together.
            if (emailSchedule.emails.length === 0) {
              emailSchedule.cron_expression = ''
            }

            // find the report in the active subscriptions to avoid sending extra data
            const relevantReport = this.activeSubscriptions.find((_) => _.id === row.id)
            if (!relevantReport) {
              throw new Error('Report not found')
            }
            updatePromises.push(
              usePropertyReportStore().update({
                ...relevantReport,
                email_schedule: emailSchedule
              })
            )
          }

          try {
            const updatedRows = await Promise.all(updatePromises)
            this.updateSelectedWithNewData(updatedRows)
          } finally {
            this.batchUpdateProgress = false
          }
        })
    },
    async changeScheduleAll(schedule: BasicSchedule[]) {
      this.batchUpdateProgress = true
      const updatePromises: Promise<unknown>[] = []
      const userEmail = this.user?.email
      const userId = this.user?.sub
      if (!userEmail || !userId) {
        throw new Error('User email or ID not found')
      }

      for (const row of this.selected) {
        const userAccess = row.access_users?.find((user) => user.id === userId)
        if (userAccess?.read_only === true) {
          continue // Skip reports where user has read_only access
        }

        const emailSchedule = row.email_schedule ?? { emails: [], cron_expression: '' }
        const currentSchedule = cronExpressionToBasicSchedule(emailSchedule.cron_expression)
        if (!areBasicSchedulesEqual(currentSchedule, schedule)) {
          // find the report in the active subscriptions to avoid sending extra data
          const relevantReport = this.activeSubscriptions.find((_) => _.id === row.id)
          if (!relevantReport) {
            throw new Error('Report not found')
          }

          // Build a new list of emails using the existing email list if it
          // exists. The user turning on the emails will be added to the list.
          const emails = relevantReport.email_schedule?.emails ?? []
          if (!emails.includes(userEmail)) {
            emails.push(userEmail)
          }

          // Convert the new schedule into a cron expression. If the schedule
          // is empty, then the user is turning off emails for the report(s).
          const newSchedule = basicScheduleToCronExpression(schedule)
          const email_schedule =
            newSchedule === null
              ? { emails: [], cron_expression: '' }
              : {
                  emails,
                  cron_expression: newSchedule
                }

          updatePromises.push(
            usePropertyReportStore().update({
              ...relevantReport,
              email_schedule
            })
          )
        }
      }

      try {
        const updatedRows = await Promise.all(updatePromises)
        this.updateSelectedWithNewData(updatedRows)
      } finally {
        this.batchUpdateProgress = false
        this.changeScheduleDialog = false
        this.scheduleDays = []
      }
    },

    async updateReportTags(report: PropertyReportSubscriptionAttributes, tags: string[]) {
      if (report.id === undefined) {
        return
      }

      this.batchUpdateProgress = true
      this.loadingTagChanges[report.id] = true
      try {
        // find the report in the active subscriptions to avoid sending extra data
        const relevantReport = this.activeSubscriptions.find((_) => _.id === report.id)
        if (!relevantReport) {
          throw new Error('Report not found')
        }
        await usePropertyReportStore().update({
          ...relevantReport,
          tags
        })
        this.$tracker.updatedReport(report.id, 'property', 'tags')
      } finally {
        this.loadingTagChanges[report.id] = false
        this.batchUpdateProgress = false
      }
    },
    humanizeDaysOfWeek(cronExpression: string): string[] {
      try {
        const { dayOfWeek, dayOfMonth } = cronParser.parse(cronExpression).fields

        // If a specific day of the month is set, then the schedule is once a month.
        if (dayOfMonth.values.length === 1) {
          return ['Monthly']
        }

        // Is everyday selected?
        if (dayOfWeek.isWildcard) {
          return ['Everyday']
        }

        // Weekends if only Saturday & Sunday
        if (
          dayOfWeek.values.length === 2 &&
          dayOfWeek.values.includes(6) &&
          // Sunday is 0 or 7
          (dayOfWeek.values.includes(0) || dayOfWeek.values.includes(7))
        ) {
          return ['Weekends']
        }

        // Weekdays if only Monday - Friday
        if (
          dayOfWeek.values.length === 5 &&
          dayOfWeek.values.includes(1) &&
          dayOfWeek.values.includes(2) &&
          dayOfWeek.values.includes(3) &&
          dayOfWeek.values.includes(4) &&
          dayOfWeek.values.includes(5)
        ) {
          return ['Weekdays']
        }

        // Return individual days at this point
        const labels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
        return dayOfWeek.values.map((_: DayOfWeekRange) => labels[(_ as number) % labels.length])
      } catch (e) {
        console.error(e)
        return []
      }
    },
    getUser(id: string) {
      return useUserStore().peopleInOrgOrSharedPerId.get(id)
    },
    async refreshSubscriptions(admin?: boolean) {
      this.loading = true
      this.isAdminView = admin ?? false
      await usePropertyReportStore()
        .refreshList(admin)
        .finally(() => {
          this.loading = false
        })
    },
    getForceRunLoading(id: number): boolean {
      return this.forceRunLoading[id]
    },
    forceRun(rowId: number) {
      this.forceRunLoading[rowId] = true

      const sendEmailFailureNotification = () => {
        this.$q.notify({
          message: 'Failed to send report. Please try again in a few minutes.',
          color: 'blue',
          position: 'top'
        })
      }

      usePropertyReportStore()
        .sendEmail(rowId)
        .then((ok) => {
          if (ok) {
            this.$q.notify({
              message: 'Report sent successfully',
              color: 'positive',
              position: 'top'
            })
          } else {
            sendEmailFailureNotification()
          }
        })
        .catch(() => {
          sendEmailFailureNotification()
        })
        .finally(() => {
          this.forceRunLoading[rowId] = false
        })
    },
    validateDelete(row: PropertyReportSubscriptionAttributes) {
      this.deleteDialog = true
      this.toDelete = row
    },
    deleteReport() {
      const deleteId = this.toDelete?.id
      if (deleteId !== undefined) {
        this.deleteLoading = true
        usePropertyReportStore()
          .delete(deleteId)
          .then(() => {
            this.$q.notify({
              message: `${this.toDelete?.name} successfully deleted`,
              color: 'positive',
              position: 'top'
            })
          })
          .catch(() => {
            this.$q.notify({
              message: `Failed to delete ${this.toDelete?.name}`,
              color: 'negative',
              position: 'top'
            })
          })
          .finally(() => {
            this.deleteLoading = false
            const idx = this.selected.findIndex((_) => _.id === deleteId)
            if (idx >= 0) {
              this.selected.splice(idx, 1)
            }
          })
      }
    },
    async copyPropertyReport(rowId: number) {
      const row = this.activeSubscriptions.find((_) => _.id === rowId)
      if (row) {
        await usePropertyReportStore().create({
          ...row,
          name: `${row.name} (Copy)`,
          is_organization_visible: false,
          organization_visibility: null,
          public_id: null
        })
      }
    },
    finishedImport(imported: number) {
      this.bulkImportDialog = false
      this.$q.notify({
        message: `CSV Import Completed: ${imported} reports generated`,
        color: 'positive',
        position: 'top'
      })
    },
    reportInOrg(row: PropertyReportSubscriptionAttributes) {
      return useUserStore().reportInOrg(row)
    },
    scrollTags(direction: -1 | 1) {
      const tagScrollArea = this.$refs['tagScrollArea'] as QScrollArea
      const newPosition = tagScrollArea.getScrollPosition().left + 500 * direction
      tagScrollArea.setScrollPosition('horizontal', newPosition)
    },
    getSelectedString() {
      return this.selected.length === 0
        ? ''
        : `${this.selected.length} record${this.selected.length > 1 ? 's' : ''} selected of ${this.filteredRows.length}`
    },
    updateSelectedWithNewData(updatedRows: (PropertyReportSubscriptionAttributes | null)[]) {
      updatedRows.forEach((row) => {
        if (row === null) {
          return
        }
        const idx = this.selected.findIndex((_) => row.id === _.id)
        if (idx === -1) {
          console.warn('Report not found in selected', row.id)
          return
        }
        this.selected[idx] = { user: this.getUser(row.user_id), ...row }
      })
    },
    allowedToLeaveReport(row: PropertyReportSubscriptionAttributes): boolean {
      if (row.user_id === this.user?.sub) {
        return false
      }
      return row.organization_visibility === null
    },
    leaveReportButtonTooltip(row: PropertyReportSubscriptionAttributes): string | null {
      if (row.user_id === this.user?.sub) {
        return 'You cannot leave a report that you own, please transfer ownership or delete the report'
      }
      if (row.organization_visibility !== null) {
        return 'You cannot leave a report that is shared with your entire organization. Contact the report owner to change your access'
      }
      return null
    },
    confirmLeaveReport(row: PropertyReportSubscriptionAttributes): void {
      this.leaveReportDialog = true
      this.reportToLeave = row
    },
    leaveReport(): void {
      this.leaveReportLoading = true
      usePropertyReportStore()
        .deleteAccess(this.reportToLeave!.id, this.user.sub)
        .then(() => {
          // remove it from selected if it's selected
          const idx = this.selected.findIndex((_) => _.id === this.reportToLeave.id)
          if (idx >= 0) {
            this.selected.splice(idx, 1)
          }
          usePropertyReportStore().removeReport(this.reportToLeave.id)
          this.$tracker.changedReportVisibility(
            this.reportToLeave.id,
            'property',
            'removed',
            'dashboard'
          )
          this.$q.notify({
            message: `You have left the ${this.reportToLeave?.name} report successfully`,
            color: 'positive',
            position: 'top'
          })
        })
        .catch(() => {
          this.$q.notify({
            message: `Failed to leave the ${this.reportToLeave?.name} report`,
            color: 'negative',
            position: 'top'
          })
        })
        .finally(() => {
          this.leaveReportLoading = false
          this.reportToLeave = null
          this.leaveReportDialog = false
        })
    },
    async copyEmailAddress(email: string) {
      await copyToClipboard(email).then(() => {
        this.$q.notify({
          message: 'Email copied to clipboard',
          color: 'primary',
          position: 'bottom'
        })
      })
    },
    addMetadataFilter() {
      this.activeMetadataFilters.push({ key: null, value: null, logic: 'contains' })
    },
    deleteSelectedReports() {
      Promise.all(this.selected.map((report) => usePropertyReportStore().delete(report.id)))
        .then(() => {
          this.$q.notify({
            message: `Successfully deleted ${this.selected.length} reports`,
            color: 'positive',
            position: 'top'
          })
          this.selected = []
        })
        .catch(() => {
          this.$q.notify({
            message: 'Failed to delete some reports',
            color: 'negative',
            position: 'top'
          })
        })
      this.deleteConfirmationText = ''
      this.deleteSelectedDialog = false
    },
    clickedNewReport(buttonPlacement: 'top_right_corner' | 'in_table_empty_state') {
      if (!this.hasPropertyReports) {
        this.displayNotSubscribed = true
      }
      this.$tracker.clickedNewReportButton(buttonPlacement)
    },
    filterByTag(tag: string) {
      if (this.filteredTags === tag) {
        this.filteredTags = null
      } else {
        this.filteredTags = tag
      }
    },
    downloadCSV() {
      if (!this.filteredRows.length) return

      try {
        const headers = [
          'Report ID',
          'Report Name',
          'Address',
          'Year Built',
          'Units',
          'Owner',
          'Shared With'
        ]

        const csvData = this.filteredRows.map((row) => {
          const userEdits = row.property_edits?.find((_) => _.property_id === row.subject?.id)
          const streetAddress = userEdits?.street_address || row.subject?.street_address || ''
          const city = row.subject?.city || ''
          const state = row.subject?.state || ''
          const zipCode = row.subject?.zip_code || ''

          const fullAddress = [streetAddress, city, state, zipCode]
            .filter((part) => part.trim() !== '')
            .join(', ')

          const yearBuilt = userEdits?.year_built || row.subject?.year_built || ''
          const units = userEdits?.number_units || row.subject?.number_units || ''
          const owner = row.user ? row.user.email : ''
          const sharedWith = row.access_users?.map((user) => user.email).join('; ') || ''

          return [row.id, row.name || '', fullAddress, yearBuilt, units, owner, sharedWith]
        })

        const escapeCsv = (value: any) => {
          const str = String(value || '')
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`
          }
          return str
        }

        const csvContent = [
          headers.join(','),
          ...csvData.map((row) => row.map(escapeCsv).join(','))
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `property-reports-${format(new Date(), 'yyyy-MM-dd')}.csv`
        link.click()
        URL.revokeObjectURL(url)

        this.$tracker.exported('dashboard')
      } catch (error) {
        console.error('Failed to download CSV:', error)
        this.$q.notify({
          message: `Failed to download CSV: ${error instanceof Error ? error.message : 'Unknown error'}`,
          color: 'negative'
        })
      }
    },
    async updatePortfolioStatus(isInPortfolio: boolean) {
      this.batchUpdateProgress = true
      const updateReportIds: number[] = []
      const updatePromises: Promise<PropertyReportSubscriptionAttributes | null>[] = []

      const selectedIds = new Set(this.selected.map((row) => row.id))
      const relevantReports = this.activeSubscriptions.filter((sub) => selectedIds.has(sub.id))

      for (const relevantReport of relevantReports) {
        if (relevantReport.is_in_portfolio === isInPortfolio) {
          continue
        }
        updateReportIds.push(relevantReport.id)
        updatePromises.push(
          usePropertyReportStore().update({
            ...relevantReport,
            is_in_portfolio: isInPortfolio
          })
        )
      }
      const action = isInPortfolio ? 'Adding' : 'Removing'
      const preposition = isInPortfolio ? 'to' : 'from'
      console.log(`${action} the following report ids ${preposition} portfolio: `, updateReportIds)
      try {
        const updatedRows = await Promise.all(updatePromises)
        this.updateSelectedWithNewData(updatedRows)
      } finally {
        this.batchUpdateProgress = false
      }
    },
    addAllToPortfolio() {
      this.$q
        .dialog({
          title: 'Add all to Portfolio',
          message: `Are you sure you want to add all of these reports to your portfolio?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => this.updatePortfolioStatus(true))
    },
    removeAllFromPortfolio() {
      this.$q
        .dialog({
          title: 'Remove all from Portfolio',
          message: `Are you sure you want to remove all of these reports from your portfolio?`,
          cancel: true,
          persistent: true
        })
        .onOk(() => this.updatePortfolioStatus(false))
    },
    userHasReadOnlyAccess(row: PropertyReportSubscriptionAttributes | undefined): boolean {
      if (!row) {
        return false
      }
      const userId = this.user?.sub
      if (!userId) {
        return false
      }

      const userAccess = row.access_users?.find((user) => user.id === userId)
      return userAccess?.read_only ?? false
    }
  }
}
</script>

<style lang="scss" scoped>
.text-elipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.property-compare {
  max-width: 1600px;
}
.property-link {
  text-decoration: none;
  color: inherit;
  font-weight: 500;

  &:hover {
    color: $primary;
  }
}
.small-chip {
  font-size: 12px;
  padding: 4px 8px;
}

.stacked-avatars {
  position: relative;
  max-width: 120px;

  &:hover {
    .shares-avatar:nth-child(n + 1) {
      transition: 100ms ease;
      margin-right: 15px;
    }
  }
}
</style>

<style lang="scss">
.dashboard-table {
  /* height or max-height is important */
  max-height: 75vh;

  thead tr th {
    position: sticky;
    z-index: 1;
  }

  thead tr:first-child th {
    top: 0;
  }

  /* this is when the loading indicator appears */
  &.q-table--loading thead tr:last-child th {
    /* height of all previous header rows */
    top: 28px;
  }

  /* prevent scrolling behind sticky top row on focus */
  tbody {
    /* height of all previous header rows */
    scroll-margin-top: 28px;
  }
}

.active-chip {
  border: 2px solid $primary;
}
</style>
